"use client";

import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import type { OpenAccessStrings } from "@/components/tools/open-access-finder";
import {
  Badge,
  type BadgeTone,
} from "@/components/tools/reference-tools/badge";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import type {
  DoiLine,
  OaLocation,
  OaRecord,
  OaStatus,
} from "@/lib/tool-kit/reference-tools/open-access";

/*
 * The Open Access Finder's table: one row per pasted line, with the status OpenAlex
 * reports and its best free copy. Every word comes from the widget's strings.
 */

export type OaRowState =
  | "invalid"
  | "duplicate"
  | "over"
  | "queued"
  | "found"
  | "missing"
  | "failed";

export interface OaRow extends DoiLine {
  state: OaRowState;
  record?: OaRecord;
}

const TONES: Record<OaStatus, BadgeTone> = {
  diamond: "good",
  gold: "good",
  hybrid: "info",
  bronze: "info",
  green: "good",
  closed: "neutral",
};

const LINK = "break-all text-primary underline-offset-4 hover:underline";

function licenseText(
  license: string | null,
  strings: OpenAccessStrings
): string {
  if (!license) {
    return strings.noLicense;
  }
  return (strings.licenses as Record<string, string>)[license] ?? license;
}

function detailsText(best: OaLocation, strings: OpenAccessStrings): string {
  return [
    best.version ? strings.versions[best.version] : strings.noVersion,
    licenseText(best.license, strings),
    best.host,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** The table as tab-separated text, for a spreadsheet. */
export function oaTsv(
  rows: readonly OaRow[],
  strings: OpenAccessStrings
): string {
  const head = [
    strings.columns.doi,
    strings.columns.work,
    strings.columns.status,
    strings.landingPage,
    strings.pdf,
    strings.columns.details,
  ];
  const body = rows.map((row) => {
    const record = row.record;
    const best = record?.best ?? null;
    return [
      row.doi ?? row.input,
      record ? `${record.title}${record.year ? ` (${record.year})` : ""}` : "",
      record ? strings.statuses[record.status] : rowStateText(row, strings),
      best?.landingUrl ?? "",
      best?.pdfUrl ?? "",
      best ? detailsText(best, strings) : "",
    ].join("\t");
  });
  return [head.join("\t"), ...body].join("\n");
}

function rowStateText(row: OaRow, strings: OpenAccessStrings): string {
  if (row.state === "found") {
    return "";
  }
  return strings.rowStates[row.state];
}

function CopyLinks({
  record,
  strings,
}: {
  record: OaRecord;
  strings: OpenAccessStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const best = record.best;
  if (!best) {
    return null;
  }
  const others = Math.max(0, record.openCopies - 1);
  return (
    <div className="flex flex-col gap-1">
      {best.landingUrl ? (
        <a
          className={LINK}
          href={best.landingUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          {strings.landingPage}
        </a>
      ) : null}
      {best.pdfUrl ? (
        <a
          className={LINK}
          href={best.pdfUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          {strings.pdf}
        </a>
      ) : null}
      {others > 0 ? (
        <span className="text-muted-foreground text-xs">
          {tPlural(strings.moreCopies, locale, others, { count: fmt(others) })}
        </span>
      ) : null}
    </div>
  );
}

function ResultRow({
  row,
  strings,
}: {
  row: OaRow;
  strings: OpenAccessStrings;
}) {
  const record = row.record;
  return (
    <tr>
      <td className={`${TD} font-mono text-xs`}>
        {row.doi ? (
          <a
            className={LINK}
            href={`https://doi.org/${row.doi}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            {row.doi}
          </a>
        ) : (
          <span className="break-all">{row.input}</span>
        )}
      </td>
      <td className={`${TD} min-w-48`}>
        {record ? (
          <span>
            {record.title}
            {record.year ? (
              <span className="text-muted-foreground"> ({record.year})</span>
            ) : null}
          </span>
        ) : (
          <span className="text-muted-foreground">
            {rowStateText(row, strings)}
          </span>
        )}
      </td>
      <td className={TD}>
        {record ? (
          <div className="space-y-1">
            <Badge tone={TONES[record.status]}>
              {strings.statuses[record.status]}
            </Badge>
            <p className="text-muted-foreground text-xs">
              {strings.statusMeaning[record.status]}
            </p>
          </div>
        ) : null}
      </td>
      <td className={TD}>
        {record ? <CopyLinks record={record} strings={strings} /> : null}
      </td>
      <td className={`${TD} text-muted-foreground text-xs`}>
        {record?.best ? detailsText(record.best, strings) : null}
      </td>
    </tr>
  );
}

export function OaResultTable({
  rows,
  strings,
}: {
  rows: readonly OaRow[];
  strings: OpenAccessStrings;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.columns.doi}
            </th>
            <th className={TH} scope="col">
              {strings.columns.work}
            </th>
            <th className={TH} scope="col">
              {strings.columns.status}
            </th>
            <th className={TH} scope="col">
              {strings.columns.copy}
            </th>
            <th className={TH} scope="col">
              {strings.columns.details}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <ResultRow key={row.line} row={row} strings={strings} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
